/**
 * SSO Authentication Service
 *
 * Implements OAuth2 Authorization Code Flow against the
 * SVDY Authorization Service.
 *
 * Flow:
 *   1. redirectToAuthorize() redirects to:
 *        GET {AUTH_URL}/api/v1/auth/authorize?response_type=code&client_id=...
 *            &redirect_uri=<frontend/auth/callback>&scope=openid+profile+email&state=<random>
 *   2. Auth server checks session cookie:
 *        - Has session → generates code, redirects back immediately (SSO).
 *        - No session  → shows sign-in form, sets session cookie, then generates code.
 *   3. SSOCallback receives code at /auth/callback, exchanges for tokens.
 *   4. Tokens are stored and the user is navigated to their original destination.
 */

const SSO_BASE     = process.env.REACT_APP_AUTH_URL     || 'http://127.0.0.1:8000';
const CLIENT_ID    = process.env.REACT_APP_CLIENT_ID    || '';
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || `${window.location.origin}/auth/callback`;

const STORAGE_KEYS = {
  accessToken:  'access_token',
  refreshToken: 'refresh_token',
  idToken:      'id_token',
  expiresAt:    'token_expires_at',
} as const;

const RETURN_TO_KEY   = 'return_to';
const OAUTH_STATE_KEY = 'oauth_state';

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.accessToken);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.refreshToken);
}

export function getIdToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.idToken);
}

export function isAuthenticated(): boolean {
  const token = getAccessToken();
  if (!token) return false;

  const expiresAt = localStorage.getItem(STORAGE_KEYS.expiresAt);
  return !expiresAt || Date.now() <= Number(expiresAt);
}

/** Returns the millisecond timestamp when the access token expires, or null. */
export function getTokenExpiresAt(): number | null {
  const v = localStorage.getItem(STORAGE_KEYS.expiresAt);
  return v ? Number(v) : null;
}

export function storeTokens(
  accessToken: string,
  refreshToken: string,
  expiresIn: number,
  idToken?: string,
): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
  localStorage.setItem(STORAGE_KEYS.expiresAt, String(Date.now() + expiresIn * 1000));
  if (idToken) {
    localStorage.setItem(STORAGE_KEYS.idToken, idToken);
  } else {
    localStorage.removeItem(STORAGE_KEYS.idToken);
  }
}

export function clearTokens(): void {
  Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
  // Clean up legacy keys from previous implementation
  ['sso_access_token', 'sso_refresh_token', 'sso_expires_at', 'token'].forEach(
    (k) => localStorage.removeItem(k),
  );
  localStorage.removeItem('current_organization_id');
  localStorage.removeItem('current_role_code');
}

// ---------------------------------------------------------------------------
// Return-to URL (saved before SSO redirect so the user lands back correctly)
// ---------------------------------------------------------------------------

const RETURN_ORIGIN = 'https://return-to.invalid';
const AUTH_PATH = /^\/auth(?:\/|$)/;

function hasControlChars(value: string): boolean {
  return Array.from(value).some((char) => {
    const code = char.charCodeAt(0);
    return code <= 31 || code === 127;
  });
}

/** Accept only same-origin application paths and reject auth loops. */
export function normalizeSafeReturnTo(
  value: string | null | undefined,
  fallback = '',
): string {
  if (!value || hasControlChars(value) || value.includes('\\')) return fallback;
  if (!value.startsWith('/') || value.startsWith('//')) return fallback;

  let decoded = value;
  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      return fallback;
    }
  }

  if (
    hasControlChars(decoded) ||
    decoded.includes('\\') ||
    !decoded.startsWith('/') ||
    decoded.startsWith('//')
  ) {
    return fallback;
  }

  try {
    const parsed = new URL(value, RETURN_ORIGIN);
    const decodedParsed = new URL(decoded, RETURN_ORIGIN);
    if (parsed.origin !== RETURN_ORIGIN || decodedParsed.origin !== RETURN_ORIGIN) {
      return fallback;
    }
    if (AUTH_PATH.test(parsed.pathname) || AUTH_PATH.test(decodedParsed.pathname)) {
      return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

export function saveReturnTo(
  path: string,
  storage: Pick<Storage, 'setItem'> = window.sessionStorage,
): void {
  const safePath = normalizeSafeReturnTo(path);
  if (!safePath) return;
  try {
    storage.setItem(RETURN_TO_KEY, safePath);
  } catch {
    // Private/sandboxed browsing may disable storage. Authentication still
    // succeeds; only the deep-link fallback is lost.
  }
}

export function saveCurrentReturnTo(
  location: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location,
  storage: Pick<Storage, 'setItem'> = window.sessionStorage,
): void {
  saveReturnTo(`${location.pathname}${location.search}${location.hash}`, storage);
}

export function popReturnTo(
  fallback = '/dashboard',
  storage: Pick<Storage, 'getItem' | 'removeItem'> = window.sessionStorage,
): string {
  try {
    const path = storage.getItem(RETURN_TO_KEY);
    storage.removeItem(RETURN_TO_KEY);
    return normalizeSafeReturnTo(path, fallback);
  } catch {
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// OAuth2 state (CSRF protection)
// ---------------------------------------------------------------------------

export function saveOAuthState(state: string): void {
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
}

export function popOAuthState(): string | null {
  const s = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  return s;
}

const OAUTH_PKCE_VERIFIER_PREFIX = 'oauth_pkce_verifier:';

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function createPkceChallenge(state: string): Promise<string> {
  const verifierBytes = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64Url(verifierBytes);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  sessionStorage.setItem(`${OAUTH_PKCE_VERIFIER_PREFIX}${state}`, verifier);
  return base64Url(new Uint8Array(digest));
}

function popPkceVerifier(state: string): string | null {
  const key = `${OAUTH_PKCE_VERIFIER_PREFIX}${state}`;
  const verifier = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  return verifier;
}

// ---------------------------------------------------------------------------
// SSO redirect — OAuth2 Authorization Code Flow
// ---------------------------------------------------------------------------

/**
 * Redirect the browser to the auth server's /authorize endpoint.
 *
 * This is the **standard OAuth2 entry point**. The auth server checks for an
 * existing session cookie:
 *   - Has session → generates code, redirects back immediately (**SSO**).
 *   - No session  → shows the sign-in form internally, then redirects.
 *
 * **Why /authorize and not /sign-in?**
 * Redirecting to /sign-in bypasses the session check and always shows the
 * login form — breaking SSO for users who already signed in via another app.
 */
export async function redirectToAuthorize(
  email?: string,
  options?: { prompt?: 'none' | 'login' },
): Promise<void> {
  if (process.env.NODE_ENV === 'development') {
    if (!CLIENT_ID) console.warn('[authService] REACT_APP_CLIENT_ID is not set — restart the dev server after editing .env');
    if (!REDIRECT_URI) console.warn('[authService] REACT_APP_REDIRECT_URI is not set');
  }

  // Generate and persist state for CSRF verification in SSOCallback
  const state = crypto.randomUUID();
  saveOAuthState(state);
  const codeChallenge = await createPkceChallenge(state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             CLIENT_ID,
    redirect_uri:          REDIRECT_URI,
    scope:                 'openid profile email',
    state,
    code_challenge:        codeChallenge,
    code_challenge_method: 'S256',
  });

  // Pass email hint so the auth server can pre-fill the sign-in form
  if (email) params.set('email', email);

  // OIDC prompt. `none` = silent SSO probe: the auth server answers with a
  // code when this browser already has an SVDY session, and with
  // error=login_required / consent_required otherwise — it never renders an
  // interactive page. That is what lets a user who signed in at svdy.com open
  // QuTime without being asked for the email they just signed in with.
  if (options?.prompt) params.set('prompt', options.prompt);

  // Pass current locale so the auth server's hosted UI (sign-in / signup /
  // forgot-password / verify / authorize / accept-invite templates) renders
  // in the same language the user chose in onboarding. The auth server reads
  // ?lang= and sets a `svdy_locale` cookie on id.svdy.com so subsequent
  // direct visits (e.g. password reset link) honor the same locale.
  // Falls back to localStorage 'i18nextLng' (set by react-i18next), then
  // navigator.language, then 'en'.
  try {
    const lang =
      (typeof window !== 'undefined' && window.localStorage.getItem('i18nextLng')) ||
      (typeof navigator !== 'undefined' && navigator.language) ||
      'en';
    if (lang) params.set('lang', lang);
  } catch {
    // localStorage access may throw in private browsing or sandboxed contexts;
    // ignore and let the auth server resolve via Accept-Language header.
  }

  // Use replace() so the SSO redirect does NOT create a browser history entry.
  window.location.replace(`${SSO_BASE}/api/v1/auth/authorize?${params.toString()}`);
}

/**
 * Redirect the browser directly to the auth server's `/signup` page.
 *
 * Why direct (not via /authorize): /authorize routes unauthenticated users
 * to /sign-in by default. The "Create a new account" CTA needs to land on
 * /signup (skip the extra "click sign-up link" step the user complained
 * about). After the user completes signup + email verification, the auth
 * server redirects back to the authorize URL we pre-encode here, which
 * THEN issues the auth code → SSOCallback exchanges it for tokens →
 * normal post-login flow continues.
 *
 * Param contract matches /signup template handler in
 * router_hosted_ui.py::sso_signup_page.
 */
export async function redirectToSignup(email?: string): Promise<void> {
  if (process.env.NODE_ENV === 'development') {
    if (!CLIENT_ID) console.warn('[authService] REACT_APP_CLIENT_ID is not set');
    if (!REDIRECT_URI) console.warn('[authService] REACT_APP_REDIRECT_URI is not set');
  }

  // Build the post-signup authorize URL (same shape as redirectToAuthorize).
  // After signup completes the auth server routes the browser to this URL,
  // which already has user session cookie → authorize issues code → SSOCallback.
  const state = crypto.randomUUID();
  saveOAuthState(state);
  const codeChallenge = await createPkceChallenge(state);

  const authorizeParams = new URLSearchParams({
    response_type:         'code',
    client_id:             CLIENT_ID,
    redirect_uri:          REDIRECT_URI,
    scope:                 'openid profile email',
    state,
    code_challenge:        codeChallenge,
    code_challenge_method: 'S256',
  });
  if (email) authorizeParams.set('email', email);

  const authorizeUrl = `${SSO_BASE}/api/v1/auth/authorize?${authorizeParams.toString()}`;

  // /signup expects: redirect (required), client_id (optional), email (optional), lang (optional)
  const signupParams = new URLSearchParams({
    redirect:  authorizeUrl,
    client_id: CLIENT_ID,
  });
  if (email) signupParams.set('email', email);
  try {
    const lang =
      (typeof window !== 'undefined' && window.localStorage.getItem('i18nextLng')) ||
      (typeof navigator !== 'undefined' && navigator.language) ||
      'en';
    if (lang) signupParams.set('lang', lang);
  } catch {
    // ignore
  }

  window.location.replace(`${SSO_BASE}/signup?${signupParams.toString()}`);
}

/**
 * @deprecated Use `redirectToAuthorize()` instead. Kept for backward compatibility.
 */
export function redirectToSSO(
  _type?: 'sign-in' | 'signup' | 'forgot-password',
  email?: string,
): void {
  void redirectToAuthorize(email);
}

/**
 * Exchange an authorization code for tokens (called from SSOCallback).
 */
export async function exchangeCodeForTokens(
  code: string,
  state: string,
): Promise<{ access_token: string; refresh_token: string; expires_in: number; id_token?: string } | null> {
  const codeVerifier = popPkceVerifier(state);
  if (!codeVerifier) {
    console.error('[authService] token exchange blocked: missing PKCE verifier');
    return null;
  }

  try {
    const body = new URLSearchParams({
      grant_type:   'authorization_code',
      code,
      redirect_uri: REDIRECT_URI,
      client_id:    CLIENT_ID,
      code_verifier: codeVerifier,
    });

    const res = await fetch(`${SSO_BASE}/api/v1/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!res.ok) {
      console.error('[authService] token exchange failed', res.status, await res.text());
      return null;
    }
    return res.json();
  } catch (err) {
    console.error('[authService] token exchange error', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Token refresh (RFC 7009 compliant)
// ---------------------------------------------------------------------------

export type RefreshSessionResult = 'refreshed' | 'missing' | 'invalid' | 'unavailable';

type WebLockManager = {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
};

let refreshSessionPromise: Promise<RefreshSessionResult> | null = null;

async function performRefresh(submittedRefreshToken: string): Promise<RefreshSessionResult> {
  try {
    const res = await fetch(`${SSO_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: submittedRefreshToken }),
    });

    // Another tab may have completed rotation while this request was in flight.
    // A late failure must never destroy or replace the newer session.
    if (getRefreshToken() !== submittedRefreshToken) {
      return getAccessToken() ? 'refreshed' : 'missing';
    }

    if (!res.ok) {
      return res.status === 400 || res.status === 401 ? 'invalid' : 'unavailable';
    }

    const data: { access_token: string; refresh_token: string; expires_in: number } =
      await res.json();
    storeTokens(data.access_token, data.refresh_token, data.expires_in);
    return 'refreshed';
  } catch {
    return getRefreshToken() !== submittedRefreshToken && getAccessToken()
      ? 'refreshed'
      : 'unavailable';
  }
}

async function refreshWithCrossTabLock(): Promise<RefreshSessionResult> {
  const submittedRefreshToken = getRefreshToken();
  if (!submittedRefreshToken) return 'missing';

  const locks = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { locks?: WebLockManager }).locks
    : undefined;

  if (!locks) return performRefresh(submittedRefreshToken);

  return locks.request('svdy-auth-refresh', async () => {
    const currentRefreshToken = getRefreshToken();
    if (currentRefreshToken !== submittedRefreshToken) {
      return getAccessToken() ? 'refreshed' : 'missing';
    }
    return performRefresh(currentRefreshToken);
  });
}

/** The single refresh owner used by every client, timer and cold-start path. */
export function refreshSession(): Promise<RefreshSessionResult> {
  if (!refreshSessionPromise) {
    refreshSessionPromise = refreshWithCrossTabLock().finally(() => {
      refreshSessionPromise = null;
    });
  }
  return refreshSessionPromise;
}

/** Backward-compatible boolean facade for existing callers. */
export async function refreshAccessToken(): Promise<boolean> {
  return (await refreshSession()) === 'refreshed';
}

// ---------------------------------------------------------------------------
// Step-up re-authentication (ADR-014 D6)
// ---------------------------------------------------------------------------

/**
 * Re-authenticate the user to stamp a fresh `auth_time` on their tokens.
 *
 * Called by the StepUpModal when the backend returns 401 with
 * `detail.error === "step_up_required"`. Issues new access + refresh tokens
 * via `POST /auth/reauthenticate` and persists them to localStorage so the
 * platform client can retry the original request immediately.
 *
 * Throws on failure so the modal can surface the error to the user.
 */
export async function reauthenticate(password: string): Promise<{
  access_token: string;
  refresh_token: string;
  expires_in: number;
  id_token?: string;
}> {
  const token = getAccessToken();
  if (!token) {
    const err: Error & { status?: number } = new Error('Not authenticated');
    err.status = 401;
    throw err;
  }

  const res = await fetch(`${SSO_BASE}/api/v1/auth/reauthenticate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ password }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const err: Error & {
      status?: number;
      body?: unknown;
    } = new Error(
      (body as { detail?: string } | null)?.detail ||
        `Reauthenticate failed (${res.status})`,
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }

  const data: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    id_token?: string;
  } = await res.json();
  storeTokens(data.access_token, data.refresh_token, data.expires_in, data.id_token);
  return data;
}

// ---------------------------------------------------------------------------
// Sign-out
// ---------------------------------------------------------------------------

/**
 * Revoke this browser's grant on the backend (RFC 7009). Best-effort —
 * fire-and-forget.
 *
 * Presents the REFRESH token: per RFC 7009 §2.1 that invalidates the whole
 * grant, whereas the access token only blacklists one short-lived string and
 * leaves the 30-day refresh chain usable. It also keeps the revocation scoped
 * to this browser — the user's other devices hold different chains.
 */
export function revokeToken(): void {
  const token = getAccessToken();
  const refreshToken = getRefreshToken();
  if (!token && !refreshToken) return;
  const body = new URLSearchParams();
  if (refreshToken) {
    body.set('token', refreshToken);
    body.set('token_type_hint', 'refresh_token');
  }
  fetch(`${SSO_BASE}/api/v1/auth/token/revocation`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
    keepalive: true,
  }).catch(() => { /* best-effort */ });
}

/**
 * Global Logout — destroys the auth server session.
 *
 * 1. Revokes the access token (best-effort).
 * 2. Clears local tokens.
 * 3. Redirects browser to GET /sign-out with post_logout_redirect_uri.
 *    Auth server destroys session cookie and 302s back to the app root.
 *
 * After this, the user must re-enter credentials on the next sign-in
 * from **any** frontend.
 */
export function redirectToSignOut(): void {
  revokeToken();
  clearTokens();

  // GET redirect — auth server destroys session cookie, then 302s back.
  const params = new URLSearchParams({
    post_logout_redirect_uri: window.location.origin,
    // OIDC RP-Initiated Logout `client_id`: scopes refresh revocation to this
    // relying party, so signing out of the admin SPA does not end the same
    // user's session in the mobile app.
    client_id: CLIENT_ID,
  });
  window.location.replace(`${SSO_BASE}/api/v1/auth/sign-out?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Authenticated fetch helper (auto-refreshes expired tokens)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// User profile
// ---------------------------------------------------------------------------

export interface UserProfile {
  id: string;
  email: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  job_title: string | null;
  department: string | null;
  is_active: boolean;
  is_verified: boolean;
  is_superuser: boolean;
}

/** Fetch the authenticated user's profile from the OIDC /userinfo endpoint. */
export async function fetchUserProfile(): Promise<UserProfile | null> {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const res = await fetch(`${SSO_BASE}/api/v1/auth/userinfo`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

/**
 * A drop-in for `fetch` that automatically attaches the Bearer token and
 * retries once if the token has expired (401).
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const token = getAccessToken();

  const makeRequest = (t: string | null) => {
    const headers = new Headers(init.headers);
    if (t) headers.set('Authorization', `Bearer ${t}`);
    return fetch(input, { ...init, headers });
  };

  let res = await makeRequest(token);

  if (res.status === 401) {
    const refreshResult = await refreshSession();
    if (refreshResult === 'refreshed') {
      res = await makeRequest(getAccessToken());
    } else if (refreshResult === 'invalid' || refreshResult === 'missing') {
      saveCurrentReturnTo();
      clearTokens();
      // Navigate to Onboarding — use replace() to avoid polluting history.
      window.location.replace('/onboarding');
    }
  }

  return res;
}
